import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { createTheme, Theme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Extend Material-UI Typography interface to include custom variants
declare module "@mui/material/styles" {
  interface TypographyVariants {
    arabic: React.CSSProperties;
    quran: React.CSSProperties;
  }

  // Allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    arabic?: React.CSSProperties;
    quran?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    arabic: true;
    quran: true;
  }
}

/**
 * Open E Masjid color palette
 */
export const EMASJID_COLORS = {
  // Primary blue - Updated to be more vibrant
  blue: "#2563EB", // Tailwind blue-600
  // Teal/Cyan accent - Updated
  teal: "#0D9488", // Tailwind teal-600
  // Button blue
  buttonBlue: "#3B82F6", // Tailwind blue-500
  // Text colors
  textPrimary: "#111827", // Tailwind gray-900
  textSecondary: "#4B5563", // Tailwind gray-600
  textTertiary: "#9CA3AF", // Tailwind gray-400
  // Background
  background: "#FFFFFF",
  backgroundGray: "#F3F4F6", // Tailwind gray-100
  // Borders
  border: "#E5E7EB", // Tailwind gray-200
  borderDark: "#D1D5DB", // Tailwind gray-300
} as const;

/**
 * Malaysian flag-inspired colors
 */
export const MALAYSIAN_COLORS = {
  // Malaysian red
  red: "#CC0001",
  // Malaysian blue
  blue: "#010066",
  // Malaysian yellow
  yellow: "#FFCC00",
  // White
  white: "#FFFFFF",
} as const;

/**
 * Theme mode type
 */
export type ThemeMode = "light" | "dark";

/**
 * Custom color overrides
 */
interface CustomColors {
  primary?: {
    main?: string;
    light?: string;
    dark?: string;
  };
  secondary?: {
    main?: string;
    light?: string;
    dark?: string;
  };
}

/**
 * Theme options
 */
interface MasjidThemeOptions {
  highContrast?: boolean;
}

/**
 * Create the Masjid-themed Material-UI theme
 */
export function createMasjidTheme(
  mode: ThemeMode = "light",
  customColors?: CustomColors,
  options?: MasjidThemeOptions
): Theme {
  const isHighContrast = options?.highContrast || false;

  const baseTheme = createTheme({
    palette: {
      mode,
      primary: {
        main: customColors?.primary?.main || EMASJID_COLORS.blue,
        light: customColors?.primary?.light || "#60A5FA",
        dark: customColors?.primary?.dark || "#1D4ED8",
        contrastText: "#FFFFFF",
      },
      secondary: {
        main: customColors?.secondary?.main || EMASJID_COLORS.teal,
        light: customColors?.secondary?.light || "#2DD4BF",
        dark: customColors?.secondary?.dark || "#0F766E",
        contrastText: "#FFFFFF",
      },
      background: {
        default: mode === "light" ? "#F9FAFB" : "#111827",
        paper: mode === "light" ? "#FFFFFF" : "#1F2937",
      },
      text: {
        primary:
          mode === "light"
            ? isHighContrast
              ? "#000000"
              : "#1B263B"
            : isHighContrast
              ? "#FFFFFF"
              : "#E0E0E0",
        secondary:
          mode === "light"
            ? isHighContrast
              ? "#333333"
              : "#0D7377"
            : isHighContrast
              ? "#CCCCCC"
              : "#B0BEC5",
      },
      error: {
        main: "#D32F2F",
        light: "#EF5350",
        dark: "#C62828",
      },
      warning: {
        main: "#ED6C02",
        light: "#FF9800",
        dark: "#E65100",
      },
      info: {
        main: EMASJID_COLORS.teal,
        light: "#26A69A",
        dark: "#00695C",
      },
      success: {
        main: EMASJID_COLORS.teal,
        light: "#6EECBB",
        dark: "#2DB885",
      },
    },
    typography: {
      fontFamily: [
        '"Inter"',
        '"Roboto"',
        '"Noto Sans Arabic"',
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
        '"Noto Color Emoji"',
      ].join(","),
      h1: {
        fontWeight: 600,
        fontSize: "2.5rem",
        lineHeight: 1.2,
      },
      h2: {
        fontWeight: 600,
        fontSize: "2rem",
        lineHeight: 1.3,
      },
      h3: {
        fontWeight: 600,
        fontSize: "1.75rem",
        lineHeight: 1.4,
      },
      h4: {
        fontWeight: 500,
        fontSize: "1.5rem",
        lineHeight: 1.4,
      },
      h5: {
        fontWeight: 500,
        fontSize: "1.25rem",
        lineHeight: 1.5,
      },
      h6: {
        fontWeight: 500,
        fontSize: "1.1rem",
        lineHeight: 1.5,
      },
      body1: {
        fontSize: "1rem",
        lineHeight: 1.6,
      },
      body2: {
        fontSize: "0.875rem",
        lineHeight: 1.5,
      },
      // Custom typography variants
      arabic: {
        fontFamily: [
          '"Noto Sans Arabic"',
          '"Traditional Arabic"',
          '"Times New Roman"',
          "serif",
        ].join(","),
        direction: "rtl",
        fontSize: "1.1rem",
        lineHeight: 1.8,
      },
      quran: {
        fontFamily: [
          '"Uthmanic Hafs"',
          '"KFGQPC Uthmanic Script HAFS"',
          '"Noto Sans Arabic"',
          "serif",
        ].join(","),
        direction: "rtl",
        fontSize: "1.3rem",
        lineHeight: 2,
        fontWeight: 400,
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    spacing: 8, // 8px base spacing
    shape: {
      borderRadius: 12, // Modern rounded corners
    },
  });

  // Component overrides
  const theme = createTheme(baseTheme, {
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: mode === "light" ? "#f1f1f1" : "#2b2b2b",
            },
            "&::-webkit-scrollbar-thumb": {
              background: EMASJID_COLORS.blue,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: baseTheme.palette.primary.dark,
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: `linear-gradient(135deg, ${EMASJID_COLORS.blue} 0%, ${EMASJID_COLORS.teal} 100%)`,
            boxShadow: "0 2px 8px rgba(58, 160, 255, 0.15)",
            "&.MuiAppBar-colorPrimary": {
              background: `linear-gradient(135deg, ${EMASJID_COLORS.blue} 0%, ${EMASJID_COLORS.teal} 100%)`,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow:
              mode === "light"
                ? "0 2px 12px rgba(0,0,0,0.08)"
                : "0 2px 12px rgba(0,0,0,0.24)",
            transition:
              "box-shadow 0.3s ease-in-out, transform 0.2s ease-in-out",
            "&:hover": {
              boxShadow:
                mode === "light"
                  ? "0 4px 20px rgba(0,0,0,0.12)"
                  : "0 4px 20px rgba(0,0,0,0.32)",
              transform: "translateY(-2px)",
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: "none",
            fontWeight: 500,
            padding: "8px 24px",
            minHeight: "40px",
          },
          contained: {
            background: `linear-gradient(135deg, ${EMASJID_COLORS.blue} 0%, ${EMASJID_COLORS.teal} 100%)`,
            boxShadow: "0 2px 8px rgba(58, 160, 255, 0.3)",
            "&:hover": {
              background: `linear-gradient(135deg, ${EMASJID_COLORS.blue} 0%, ${EMASJID_COLORS.teal} 100%)`,
              boxShadow: "0 4px 16px rgba(58, 160, 255, 0.4)",
              transform: "translateY(-1px)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
            "&.MuiButton-containedPrimary": {
              background: `linear-gradient(135deg, ${EMASJID_COLORS.blue} 0%, ${EMASJID_COLORS.teal} 100%)`,
            },
          },
          outlined: {
            borderWidth: "2px",
            "&:hover": {
              borderWidth: "2px",
              backgroundColor: `${EMASJID_COLORS.blue}08`,
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 8,
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: EMASJID_COLORS.blue,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: EMASJID_COLORS.blue,
                borderWidth: "2px",
              },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 16,
          },
          colorPrimary: {
            background: `linear-gradient(135deg, ${EMASJID_COLORS.blue} 0%, ${EMASJID_COLORS.teal} 100%)`,
            color: "#FFFFFF",
          },
          colorSecondary: {
            backgroundColor: EMASJID_COLORS.teal,
            color: "#FFFFFF",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
          elevation1: {
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          },
          elevation2: {
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          },
          elevation3: {
            boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            color: EMASJID_COLORS.blue,
            fontWeight: 600,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            background: `linear-gradient(90deg, ${EMASJID_COLORS.blue} 0%, ${EMASJID_COLORS.teal} 100%)`,
            height: 3,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 500,
            "&.Mui-selected": {
              color: EMASJID_COLORS.blue,
            },
          },
        },
      },
    },
  });

  return theme;
}

/**
 * Theme context
 */
interface ThemeContextType {
  mode: ThemeMode;
  toggleMode: () => void;
  theme: Theme;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Theme provider props
 */
interface MasjidThemeProviderProps {
  children: ReactNode;
  defaultMode?: ThemeMode;
  customColors?: CustomColors;
  options?: MasjidThemeOptions;
}

/**
 * Masjid theme provider component
 */
export function MasjidThemeProvider({
  children,
  defaultMode = "light",
  customColors,
  options,
}: MasjidThemeProviderProps) {
  const [mode, setMode] = useState<ThemeMode>(defaultMode);

  // Load theme preference from localStorage
  useEffect(() => {
    const savedMode = localStorage.getItem("masjid-theme-mode") as ThemeMode;
    if (savedMode && (savedMode === "light" || savedMode === "dark")) {
      setMode(savedMode);
    }
  }, []);

  // Save theme preference to localStorage
  useEffect(() => {
    localStorage.setItem("masjid-theme-mode", mode);
  }, [mode]);

  const toggleMode = () => {
    setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  };

  const theme = createMasjidTheme(mode, customColors, options);

  const value: ThemeContextType = {
    mode,
    toggleMode,
    theme,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}

/**
 * Hook to use the Masjid theme context
 */
export function useMasjidTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useMasjidTheme must be used within a MasjidThemeProvider");
  }
  return context;
}

/**
 * Hook to use theme mode
 */
export function useThemeMode() {
  const { mode, toggleMode } = useMasjidTheme();
  return { mode, toggleMode };
}

// Re-export Material-UI theme types for convenience
export type { Theme } from "@mui/material/styles";
